import { useSuspenseQueries } from '@tanstack/react-query';

import { useDatasetResolver } from './useDatasetResolver.ts';
import type { DatasetResolver } from '#plugin-chat/lib/data-pipeline/resolver/dataset-resolver.ts';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';
import { DatasetNotFoundError } from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * Shared resolution body used by {@link useDataset} and {@link useDatasets}.
 * Delegates to {@link DatasetResolver.byName}, which translates the name
 * into the canonical `(toolName, args)` tuple and awaits the corresponding
 * TanStack Query cache entry. Runs inside a `queryFn` — outside render —
 * so the cache lookup and any cascade it triggers do not happen during
 * the render phase.
 *
 * Returns `null` (not `undefined`) for the "no producing tool call found"
 * case — `useSuspenseQuery` rejects `undefined` as cached query data.
 * Callers translate `null` back to `undefined` at the public boundary.
 */
async function resolveByName(
  outputDataset: string | undefined | null,
  resolve: DatasetResolver,
): Promise<Dataset | null> {
  if (!outputDataset) {
    return null;
  }
  try {
    // return await resolve.byName(outputDataset);
    const ret = await resolve.byName(outputDataset);
    return ret;
  } catch (err) {
    if (err instanceof DatasetNotFoundError) {
      return null;
    }
    throw err;
  }
}
/**
 * Resolve multiple datasets by `outputDataset` name in a single hook call.
 * Each name fires its own suspense query so they dedupe individually
 * against the cache — a name already resolved by a sibling
 * {@link useDataset} call reuses the same cached promise.
 *
 * Returns a Map keyed by outputDataset; missing keys map to `undefined`.
 */
export function useDatasets<const KeyType extends string | null | undefined>(
  outputDatasets: readonly KeyType[],
): Map<KeyType, Dataset | undefined> {
  const resolve = useDatasetResolver();

  const results = useSuspenseQueries({
    queries: outputDatasets.map((key) => ({
      queryKey: ['dataset-by-name', resolve.threadId, key] as const,
      queryFn: () => resolveByName(key, resolve),
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    })),
  });

  return outputDatasets.reduce((acc, key, i) => {
    acc.set(key, results[i]?.data ?? undefined);
    return acc;
  }, new Map());
}

/**
 * Resolve a dataset by its LLM-facing `outputDataset` name via the
 * {@link DatasetResolver}.
 */
export function useDataset(outputDataset: string | undefined | null): Dataset | undefined {
  return useDatasets([outputDataset]).get(outputDataset);
}
